import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, Switch, IconButton } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation } from "@apollo/client/react";

import DataGrids from "../components/DataGrid";
import AdminDrawer from "../components/AdminDrawer";
import CustomSnackbar from "../components/Snackbar";
import themestyle from "../theme";
import { GET_ADMIN_LIST } from "../graphql/query";
import { AdminListUser, GetAdminListResponse } from "../interfaces/auth.interface";
import ViewAdminDrawer from "../components/ViewAdminDrawer";
import ConfirmDialog from "../components/confirmdialogbox"
import { DELETE_ADMIN, UPDATE_APPROVAL } from "../graphql/mutation";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MSG, ADMIN_PAGE_TEXT } from "../utils/const";

const AdminManagement: React.FC = () => {
  useEffect(() => {
    document.title = ADMIN_PAGE_TEXT.PAGE_TITLE;
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");
  const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<any>(null);
  const [updateApproval] = useMutation(UPDATE_APPROVAL);


  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<{ id: string; name: string } | null>(null);


  const [deleteAdmin, { loading: deleting }] = useMutation(DELETE_ADMIN);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{ id: string, name: string, currentStatus: boolean } | null>(null);

 const { data, loading, error, refetch, previousData } = useQuery<GetAdminListResponse>(GET_ADMIN_LIST, {
  variables: {
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
    search: searchQuery || "",
  },
  fetchPolicy: "network-only",
});

  useEffect(() => {
    if (error) {
      setSnackbarMessage(error.message);
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  }, [error]);

  const displayRows = useMemo(() => {
    const baseRows: AdminListUser[] = data?.getAdminList?.data ?? [];
    const filtered = baseRows.filter((row: AdminListUser) => !deletedIds.has(row.id));
    return filtered.map((row: AdminListUser, index: number) => {
      const override = statusOverrides[row.id];
      const isActive = typeof override === "boolean" ? override : !!row.isActive;
      const serial = paginationModel.page * paginationModel.pageSize + index + 1;
      return {
        id: row.id,
        sno: String(serial).padStart(2, "0"),
        fullName: row.fullname || "-",
        email: row.email,
        phone: row.phoneNumber || "-",
        role: row.role,
        status: isActive,
        address: row.address || "",
        country: row.country || "",
        postcode: row.postcode || "",
      };
    });
  }, [data, deletedIds, statusOverrides, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (paginationModel.page !== 0 && searchQuery.trim()) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [searchQuery]);

  const handleStatusToggle = (id: string) => {
    const currentStatus = displayRows.find((row) => row.id === id)?.status ?? false;
    setStatusOverrides((prev) => ({ ...prev, [id]: !currentStatus }));
  };

  const handleDeleteClick = (id: string, name: string) => {
    setAdminToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      const { data: response }: any = await deleteAdmin({
        variables: { userId: adminToDelete.id },
      });

      if (response.deleteAdmin.success) {
        setSnackbarMessage("Admin removed successfully");
        setSnackbarType("success");
        setSnackbarOpen(true);
        refetch();
      } else {
        setSnackbarMessage(response.deleteAdmin.message);
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err: any) {
      setSnackbarMessage(err.message || "Failed to delete admin");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleView = (row: any) => {
    setSelectedAdmin(row);
    setViewDrawerOpen(true);
  };
  const handleStatusClick = (id: string, name: string, currentStatus: boolean) => {
    setStatusTarget({ id, name, currentStatus });
    setStatusConfirmOpen(true);
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget) return;
    try {
      await updateApproval({
        variables: { id: statusTarget.id, approval: !statusTarget.currentStatus }
      });
      refetch();
      setSnackbarMessage(MSG.STATUS_UPDATED_SUCCESS);
      setSnackbarOpen(true);
    } finally {
      setStatusConfirmOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "sno",
      headerName: "S.No",
      width: 60,
      sortable: false,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 0.9,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.1,
      minWidth: 160,
      sortable: false,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 0.9,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const isActive = Boolean(params.row.status);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={isActive}
              onChange={() => handleStatusClick(params.row.id, params.row.fullName, isActive)}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#9BCB4A",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#9BCB4A",
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#D4D8DD",
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: isActive ? "#111827" : "#8A95A6", fontWeight: 500 }}
            >
              {isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            aria-label="view"
            onClick={() => handleView(params.row)}
            size="small"
            sx={{
              color: "#9BCB4A",
              "&:hover": { bgcolor: "rgba(155, 203, 74, 0.16)" },
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => handleDeleteClick(params.row.id, params.row.fullName)}
            size="small"
            sx={{
              color: "#E53935",
              "&:hover": { bgcolor: "rgba(229, 57, 53, 0.12)" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "#F7FAF3",
        // border: "1px solid #E7EEDC",
        borderRadius: 3,
        // p: { xs: 2, sm: 3 },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          fontSize: "24px",
          color: "#111827",
          mb: 1, 
          pl: 1,
          mt: 4, 
        }}
      >
        Admin Management
      </Typography>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <DataGrids
          rows={displayRows}
          columns={columns}
          toolbar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onToolbarAddClick={() => setDrawerOpen(true)}
          toolbarAddLabel="Add Admin"
          toolbarShowAddIcon={false}
          paginationMode="server"
          rowCount={data?.getAdminList?.totalCount ?? previousData?.getAdminList?.totalCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          getRowId={(row) => row.id}
          onMobileView={(row) => handleView(row)}
          onMobileDelete={(row) => handleDeleteClick(row.id, row.fullName)}
          onMobileStatusToggle={(row) => handleStatusClick(row.id, row.fullName, !!row.status)}
          loading={loading}
        />
      </Box>

      <AdminDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={() => {
          setSnackbarMessage("Admin added successfully");
          setSnackbarType("success");
          setSnackbarOpen(true);
          refetch({
            page: paginationModel.page + 1,
            perPage: paginationModel.pageSize,
            search: searchQuery || "",
          });
        }}
        onError={(message) => {
          setSnackbarMessage(message);
          setSnackbarType("error");
          setSnackbarOpen(true);
        }}
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
      <ViewAdminDrawer
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        data={selectedAdmin}
        onSuccess={() => {
          setSnackbarMessage(MSG.ADMIN_UPDATED_SUCCESS);
          setSnackbarType("success");
          setSnackbarOpen(true);
          refetch();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setSnackbarType("error");
          setSnackbarOpen(true);
        }}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        icon={<DeleteOutlineIcon sx={{ fontSize: 40, color: "#d32f2f" }} />}
        message={adminToDelete ? `${MSG.ADMIN_DELETE_CONFIRM} ${adminToDelete.name}?` : ""}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        confirmButtonColor="#EB2857"
      />
      <ConfirmDialog
        open={statusConfirmOpen}

        icon={statusTarget?.currentStatus ? (
          <CancelIcon sx={{ fontSize: 50, color: "#EB2857" }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 50, color: "#A3D451" }} />
        )}
        message={
          statusTarget
            ? `${MSG.CONFIRM_ACTION_PREFIX} ${statusTarget.currentStatus ? "deactivate" : "activate"} ${statusTarget.name}?`
            : ""
        }
        onConfirm={handleConfirmStatus}
        onClose={() => setStatusConfirmOpen(false)}
        confirmText="Confirm"
        cancelText="Cancel"

        confirmButtonColor={statusTarget?.currentStatus ? "#EB2857" : "#A3D451"}
      />
    </Box>
  );
};

export default AdminManagement;
